// Shared, SSR-safe cookie-consent primitives.
//
// This module is imported from two very different places:
//   1. `instrumentation-client.ts` at module load (before React, before
//      hydration) to decide the initial capture posture for PostHog/Datadog.
//   2. Client React components (provider, banner, dialog, footer link).
//   3. The Edge proxy (`proxy.ts`) imports ONLY the country set + cookie-name
//      constants below; it must never pull in the `document`-based helpers.
//
// Every browser helper guards `typeof document === "undefined"` so it is inert
// during SSR. No React imports here on purpose.

import { env } from "@repo/env";

export const REGION_COOKIE = "terac_consent_region";
export const CONSENT_COOKIE = "terac_cookie_consent";

// Bump to invalidate every stored choice and re-prompt in-region users (e.g.
// when the cookie inventory or vendor list materially changes).
export const CONSENT_VERSION = 1;

// 6 months. Consent should be re-confirmed periodically rather than persisting
// indefinitely. The region cookie has its own (shorter) lifetime in proxy.ts.
const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

export type ConsentRegion = "required" | "not-required";

export type ConsentCategory =
  | "necessary"
  | "analytics"
  | "functional"
  | "marketing";

export type ConsentCategories = Record<ConsentCategory, boolean>;

export interface ConsentChoice {
  version: number;
  timestamp: string;
  categories: ConsentCategories;
}

// EU-27 + EEA (Iceland, Liechtenstein, Norway) + United Kingdom (UK GDPR) +
// Switzerland (Swiss FADP). ISO 3166-1 alpha-2 as reported by Vercel
// (`x-vercel-ip-country`); note Greece is GR. Kosovo (XK) is intentionally
// excluded (not EEA).
export const CONSENT_REQUIRED_COUNTRIES: ReadonlySet<string> = new Set([
  // EU-27
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA (non-EU)
  "IS",
  "LI",
  "NO",
  // UK + Switzerland
  "GB",
  "CH",
]);

/** Necessary cookies cannot be rejected; everything else defaults off. */
export function defaultCategories(): ConsentCategories {
  return {
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
  };
}

export function allCategoriesGranted(): ConsentCategories {
  return {
    necessary: true,
    analytics: true,
    functional: true,
    marketing: true,
  };
}

function readRawCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) {
    return null;
  }
  return match.slice(name.length + 1) || null;
}

/**
 * Synchronous region read. Used at module load in instrumentation-client.ts,
 * so it must not depend on React or async APIs. The cookie is written by the
 * Edge proxy on the document response, so it is already present in
 * `document.cookie` by the time any page script runs.
 */
export function readConsentRegion(): ConsentRegion {
  return readRawCookie(REGION_COOKIE) === "required"
    ? "required"
    : "not-required";
}

function isConsentChoice(value: unknown): value is ConsentChoice {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const choice = value as Partial<ConsentChoice>;
  return (
    typeof choice.version === "number" &&
    typeof choice.timestamp === "string" &&
    typeof choice.categories === "object" &&
    choice.categories !== null &&
    typeof choice.categories.necessary === "boolean" &&
    typeof choice.categories.analytics === "boolean" &&
    typeof choice.categories.functional === "boolean" &&
    typeof choice.categories.marketing === "boolean"
  );
}

/** Returns the stored choice, or null when absent, malformed, or stale. */
export function readConsentChoice(): ConsentChoice | null {
  const raw = readRawCookie(CONSENT_COOKIE);
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (!isConsentChoice(parsed) || parsed.version !== CONSENT_VERSION) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsentChoice(
  categories: ConsentCategories,
): ConsentChoice {
  const choice: ConsentChoice = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: { ...categories, necessary: true },
  };
  if (typeof document !== "undefined") {
    const value = encodeURIComponent(JSON.stringify(choice));
    document.cookie = `${CONSENT_COOKIE}=${value}; max-age=${CONSENT_COOKIE_MAX_AGE}; path=/; samesite=lax${
      env.NODE_ENV === "production" ? "; secure" : ""
    }`;
  }
  return choice;
}

/**
 * The central gate for deciding whether a script/SDK may run. Necessary cookies
 * and any out-of-region visitor are always granted; in-region non-essential
 * categories require an explicit stored opt-in.
 *
 * NOTE: this PR ships the consent UI and records the choice but does not yet
 * apply it. Enforcement is a follow-up that will distribute APPLICATION across
 * the SDK owners (each has different init/ownership semantics):
 *   - PostHog: posthog-client.tsx (carries the admin-impersonation nuance)
 *   - Datadog RUM: cookie-consent-provider.tsx
 *   - Google (GTM/GA/Ads): the root layout / a consent-gated scripts component
 * Do not consolidate these into one applier: the PostHog path depends on
 * impersonation state the others must not see, and merging them reintroduces
 * the "re-opt-in a declining user" bug.
 */
export function isCategoryGranted(
  choice: ConsentChoice | null,
  region: ConsentRegion,
  category: ConsentCategory,
): boolean {
  if (category === "necessary") {
    return true;
  }
  if (region !== "required") {
    return true;
  }
  return choice?.categories[category] === true;
}

// Cookies set by each non-essential category's vendors, by name prefix. Used to
// actively clear cookies on withdrawal so "withdraw consent" removes the
// identifiers, not just stops new capture. Datadog (`_dd_s`) is cleared by
// setTrackingConsent and Intercom (`intercom-*`) by its SDK shutdown(), so they
// are not listed here.
const CATEGORY_COOKIE_PREFIXES: Record<
  Exclude<ConsentCategory, "necessary">,
  readonly string[]
> = {
  analytics: ["_ga", "_gid", "_gat", "ph_", "dd_rum_sample"],
  functional: [],
  marketing: ["_gcl", "_gac"],
};

function cookieDeletionDomains(): readonly (string | null)[] {
  // Cover host-only cookies and cookies scoped to the registrable parent domain
  // (e.g. analytics cookies set on `.terac.com`). null => no domain attribute.
  const host = window.location.hostname;
  const domains: (string | null)[] = [null, host];
  const parts = host.split(".");
  if (parts.length >= 2) {
    domains.push(`.${parts.slice(-2).join(".")}`);
  }
  return domains;
}

/**
 * Best-effort removal of the cookies for any category the user just denied.
 * Pairs with the SDK opt-outs (which stop new writes) so withdrawal actually
 * clears non-essential identifiers. Third-party cookies on unexpected domains
 * may persist until expiry; the cookie policy documents this.
 */
export function clearCookiesForDeniedCategories(
  categories: ConsentCategories,
): void {
  if (typeof document === "undefined") {
    return;
  }
  const prefixes = (
    Object.entries(CATEGORY_COOKIE_PREFIXES) as [
      Exclude<ConsentCategory, "necessary">,
      readonly string[],
    ][]
  ).flatMap(([category, list]) => (categories[category] ? [] : list));
  if (prefixes.length === 0) {
    return;
  }

  const names = document.cookie
    .split("; ")
    .map((row) => row.split("=")[0])
    .filter((name): name is string => Boolean(name));
  const domains = cookieDeletionDomains();

  for (const name of names) {
    if (!prefixes.some((prefix) => name.startsWith(prefix))) {
      continue;
    }
    for (const domain of domains) {
      document.cookie = `${name}=; max-age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
}

export type PostHogCaptureAction =
  | "reset_and_opt_out"
  | "opt_in"
  | "opt_out"
  | "none";

/**
 * Pure reconciliation of PostHog capture state from impersonation + consent.
 * Extracted from PostHogIdentifier so the subtle interaction is unit-testable.
 *   - Admin impersonation always wins: reset + opt out (never attribute events
 *     to the impersonated user).
 *   - Otherwise capture follows analytics consent, toggling only when the
 *     current opt-out state disagrees (so we restore capture after
 *     impersonation ends for consented/out-of-region users).
 */
export function resolvePostHogCaptureAction({
  isImpersonating,
  analyticsGranted,
  hasOptedOut,
}: {
  isImpersonating: boolean;
  analyticsGranted: boolean;
  hasOptedOut: boolean;
}): PostHogCaptureAction {
  if (isImpersonating) {
    return "reset_and_opt_out";
  }
  if (analyticsGranted) {
    return hasOptedOut ? "opt_in" : "none";
  }
  return hasOptedOut ? "none" : "opt_out";
}
